// Questionnaire v2 — the upload step on the PUBLIC questionnaire page
// (question 14 of the civil-rights template; decisions 2, 8; 2026-10-21).
//
// Files go through the same virus-scanned, encrypted records pipeline as the
// records-upload link (the server's lib/leadUploadPipeline.js) and land in the
// lead's 📁 Records.  This component only moves bytes and reports results:
//   • credentials are the questionnaire's HttpOnly session cookie plus the
//     X-Qnr-Token header (the server checks both BEFORE it reads the body);
//     the token is the one the page already holds — never put in a URL;
//   • the notice ("consent") is accepted once, with initials, before the
//     first file (the page POSTs it; the server records it on the bound link);
//   • WRITE-ONLY: the server never lists what it holds.  The receipts below
//     are THIS page's memory only — never sessionStorage/localStorage
//     (decision 8: nothing stored on the device);
//   • one XHR per file, for real progress over a phone connection (fetch
//     cannot report upload progress), sequential so a refused file never
//     takes the rest of the batch with it;
//   • while anything is uploading the page disables Submit (onBusyChange),
//     because the submit freezes the file list.
//
// Never imports services/api.js (no staff session on this page).

import { useCallback, useEffect, useRef, useState } from 'react'

const ACCEPT_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'heif']
const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/jpeg,image/png,image/heic,image/heif'

function fmtBytes (n) {
  const b = Number(n) || 0
  if (b < 1024) return `${b} B`
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`
  return `${(b / 1048576).toFixed(1)} MB`
}
function extOf (name) {
  const m = /\.([A-Za-z0-9]+)$/.exec(String(name || ''))
  return m ? m[1].toLowerCase() : ''
}
function waitPhrase (seconds) {
  const s = Number(seconds) || 60
  if (s < 120) return `${s} seconds`
  const m = Math.round(s / 60)
  return m < 120 ? `${m} minute${m === 1 ? '' : 's'}` : `${Math.round(m / 60)} hours`
}

function uploadOne ({ url, token, file, onProgress }) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    const fd = new FormData()
    fd.append('file', file, file.name)
    xhr.open('POST', url)
    xhr.withCredentials = true
    xhr.setRequestHeader('X-Qnr-Token', token)
    if (xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)))
      }
    }
    const finish = (status, text) => {
      let data = null
      try { data = JSON.parse(text) } catch { data = null }
      resolve({ ok: status >= 200 && status < 300, status, data: data || {} })
    }
    xhr.onload = () => finish(xhr.status, xhr.responseText)
    xhr.onerror = () => resolve({ ok: false, status: 0, data: {} })
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: {} })
    xhr.onabort = () => resolve({ ok: false, status: 0, data: {} })
    xhr.send(fd)
  })
}

function StatusPill ({ status }) {
  const map = {
    queued: { label: 'Waiting', cls: 'bg-gray-100 text-gray-700' },
    uploading: { label: 'Sending', cls: 'bg-sky-100 text-sky-800' },
    failed: { label: 'Not sent', cls: 'bg-red-100 text-red-800' },
  }
  const s = map[status] || map.queued
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${s.cls}`}>{s.label}</span>
}

/**
 * Props:
 *   node            the upload question (label, help_text, number_label)
 *   info            /verify `uploads`
 *   token           the questionnaire token (sent as X-Qnr-Token)
 *   uploadUrl       absolute URL of POST /api/public/questionnaire/:id/upload
 *   onConsent(initials) → Promise<{ok, status, data}>   (the page POSTs it)
 *   onRefreshSession()  → Promise<boolean>   (re-mint the cookie after a 404)
 *   onBusyChange(bool)  Submit is disabled while true
 *   disabled        the whole page is read-only
 */
export default function QuestionnaireUploadStep ({ node, info, token, uploadUrl, onConsent, onRefreshSession, onBusyChange, disabled = false }) {
  const [consentNeeded, setConsentNeeded] = useState(!!(info && info.consent_required))
  const [consentChecked, setConsentChecked] = useState(false)
  const [initials, setInitials] = useState('')
  const [consentBusy, setConsentBusy] = useState(false)
  const [error, setError] = useState('')
  const [closedMsg, setClosedMsg] = useState(info && info.uploads_open === false ? (info.uploads_closed_message || 'We cannot accept files at this moment.  Please try again shortly, or call the office.') : '')
  const [receipts, setReceipts] = useState([])         // this page's memory only
  const [received, setReceived] = useState(null)       // the server's count for this questionnaire
  const queueRef = useRef([])
  const [, setTick] = useState(0)
  const pumpingRef = useRef(false)
  const inputRef = useRef(null)
  const busyRef = useRef(false)

  const maxBytes = (info && info.max_file_bytes) || 25 * 1024 * 1024
  const hint = (info && info.accepted_hint) || 'PDFs or photos of documents'
  const labelId = `qnr-lg-${node.id}`

  const bump = useCallback(() => {
    setTick(t => t + 1)
    const busy = queueRef.current.some(i => i.status === 'uploading' || i.status === 'queued')
    if (busy !== busyRef.current) { busyRef.current = busy; if (onBusyChange) onBusyChange(busy) }
  }, [onBusyChange])

  useEffect(() => () => { if (busyRef.current && onBusyChange) onBusyChange(false) }, [onBusyChange])

  // A reload drops the files still waiting (a browser never hands back a File
  // across a navigation) — say so before it happens.
  const [inFlight, setInFlight] = useState(false)
  useEffect(() => {
    if (!inFlight) return undefined
    const h = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', h)
    return () => window.removeEventListener('beforeunload', h)
  }, [inFlight])

  const pump = useCallback(async () => {
    if (pumpingRef.current) return
    pumpingRef.current = true
    setInFlight(true)
    try {
      for (;;) {
        const item = queueRef.current.find(i => i.status === 'queued')
        if (!item) break
        item.status = 'uploading'; item.pct = 0; item.error = ''
        bump()
        const send = () => uploadOne({ url: uploadUrl, token, file: item.file, onProgress: (p) => { item.pct = p; bump() } })
        let r = await send()
        // 404 is the same answer for a dead link and an expired 30-minute
        // session (deliberately indistinguishable to a stranger).  Re-verify
        // once with the token the page holds; a live link re-mints the cookie.
        if (r.status === 404 && onRefreshSession) {
          const again = await onRefreshSession()
          if (again) r = await send()
        }
        if (r.ok) {
          const receipt = r.data.receipt || {}
          item.status = 'done'; item.pct = 100
          setReceipts(list => [...list, {
            key: item.key, name: item.name, size: Number(receipt.size_bytes ?? item.size),
            at: receipt.received_at || null, duplicate: !!r.data.duplicate,
          }])
          if (typeof r.data.received === 'number') setReceived(r.data.received)
          queueRef.current = queueRef.current.filter(i => i.key !== item.key)
          bump()
          continue
        }
        item.status = 'failed'
        const msg = r.data && (r.data.message || (typeof r.data.error === 'string' && r.data.error.includes(' ') ? r.data.error : ''))
        if (r.status === 404) {
          item.error = 'The upload step has closed — the questionnaire may have been submitted, cancelled or expired.'
          setClosedMsg('The upload step is closed.  Files listed under "Sent to the firm" did arrive.')
          bump(); break
        }
        if (r.status === 409) {           // consent_required
          item.status = 'queued'; item.error = ''
          setConsentNeeded(true)
          bump(); break
        }
        if (r.status === 503) {
          item.status = 'queued'
          setClosedMsg(msg || 'We cannot accept files at this moment.  Please try again shortly, or call the office — your documents have NOT been received.')
          bump(); break
        }
        if (r.status === 429) {
          if (r.data && r.data.error === 'rejected_cap') {
            item.error = msg || 'Several files in a row were not accepted, so sending is paused for a while.'
            setClosedMsg(msg || 'Sending is paused for a while.  Please call the office if you need help.')
          } else {
            item.status = 'queued'
            setError(`Too many files at once.  Please wait about ${waitPhrase(r.data && r.data.retry_after_seconds)}, then press "Send waiting files".`)
          }
          bump(); break
        }
        if (r.data && r.data.uploads_open === false) setClosedMsg(r.data.uploads_closed_message || 'Sending is paused for a while.')
        item.error = r.status === 0
          ? 'The connection dropped before this file finished.  Press "Retry".'
          : (msg || 'That file was not accepted.  Press "Retry", or call the office.')
        bump()
      }
    } finally {
      pumpingRef.current = false
      setInFlight(false)
      bump()
    }
  }, [uploadUrl, token, onRefreshSession, bump])

  const addFiles = (fileList) => {
    const files = Array.from(fileList || [])
    if (!files.length) return
    setError('')
    for (const f of files) {
      const bad = f.size > maxBytes
        ? `This file is ${fmtBytes(f.size)}.  The limit is ${fmtBytes(maxBytes)} per file — please send it in parts, or call the office.`
        : (!ACCEPT_EXT.includes(extOf(f.name)) && !/^(image\/(jpeg|png|heic|heif)|application\/pdf)$/.test(f.type || ''))
            ? 'We can accept PDFs and photos (JPEG, PNG, HEIC).  Other kinds of files are not accepted — please send the pages themselves.'
            : ''
      queueRef.current.push({
        key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: bad ? null : f, name: f.name, size: f.size,
        status: bad ? 'failed' : 'queued', pct: 0, error: bad,
      })
    }
    bump()
    if (inputRef.current) inputRef.current.value = ''
    pump()
  }
  const retry = (key) => {
    const item = queueRef.current.find(i => i.key === key)
    if (!item || !item.file) return
    item.status = 'queued'; item.error = ''; item.pct = 0
    setError(''); bump(); pump()
  }
  const removeFromQueue = (key) => {
    queueRef.current = queueRef.current.filter(i => i.key !== key)
    bump()
  }

  const acceptConsent = async (e) => {
    e.preventDefault()
    setError(''); setConsentBusy(true)
    try {
      const r = await onConsent(initials.trim())
      if (r && r.ok) {
        setConsentNeeded(false)
        if (queueRef.current.some(i => i.status === 'queued')) pump()
        return
      }
      setError((r && r.data && (r.data.error || r.data.message)) || 'Please type your initials to continue.')
    } catch {
      setError('Could not reach the server.  Please try again.')
    } finally { setConsentBusy(false) }
  }

  const q = queueRef.current
  const waiting = q.filter(i => i.status === 'queued').length
  const uploading = q.some(i => i.status === 'uploading')
  const stalled = waiting > 0 && !uploading && !consentNeeded
  const open = !closedMsg && !disabled
  const lines = Array.isArray(info && info.consent_copy) ? info.consent_copy : []

  return (
    <div role="group" aria-labelledby={labelId} className="border-2 border-dashed border-gray-300 rounded-xl p-4 bg-gray-50 space-y-4">
      <div>
        <div id={labelId} className="block text-base font-medium text-gray-900 break-words">
          {node.number_label && <span className="font-semibold text-gray-700 mr-1">{node.number_label}.</span>}
          <span>{node.label}</span>
        </div>
        <p className="text-sm text-gray-600 mt-1">{hint} · up to {fmtBytes(maxBytes)} each{info && info.max_files ? ` · up to ${info.max_files} files` : ''}.  Sending files is optional.</p>
        {/* Decision Q5: these files follow the answers — the server's notice
            says when they are removed both ways (unreturned / declined). */}
        {info && (info.retention_notice || info.retention_date_display) && (
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
            {info.retention_notice || `If you do not submit this questionnaire, these files are removed from our systems on ${info.retention_date_display}.`}  Please keep your own originals.
          </p>
        )}
      </div>

      {closedMsg && <div role="alert" className="text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">{closedMsg}</div>}
      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      {open && consentNeeded && (
        <form onSubmit={acceptConsent} className="space-y-3 bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-sm font-semibold text-gray-800">Before you send documents</div>
          <ul className="space-y-2">
            {lines.map((line, i) => (
              <li key={i} className="text-sm text-gray-800 leading-relaxed flex gap-2">
                <span className="text-gray-400 flex-shrink-0" aria-hidden="true">•</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <label className="flex items-start gap-3 text-sm text-gray-800 cursor-pointer min-h-[44px]">
            <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <span>I have read the above and I understand that sending documents does not make me a client.</span>
          </label>
          <div>
            <label htmlFor={`qnr-initials-${node.id}`} className="block text-sm font-medium text-gray-700 mb-1">Type your initials</label>
            <input id={`qnr-initials-${node.id}`} value={initials} onChange={e => setInitials(e.target.value.slice(0, 8))}
              autoComplete="off" placeholder="e.g. JM"
              className="w-32 py-2.5 px-3 border border-gray-300 rounded-lg text-base uppercase tracking-widest" />
          </div>
          <button type="submit" disabled={consentBusy || !consentChecked || initials.trim().length < 2}
            className="w-full sm:w-auto min-h-[44px] px-5 rounded-lg bg-primary-600 text-white font-semibold disabled:opacity-50">
            {consentBusy ? 'Saving…' : 'I agree — continue'}
          </button>
        </form>
      )}

      {open && !consentNeeded && (
        <div>
          <input ref={inputRef} id={`qnr-files-${node.id}`} type="file" multiple accept={ACCEPT_ATTR}
            className="sr-only" onChange={e => addFiles(e.target.files)} />
          <label htmlFor={`qnr-files-${node.id}`}
            className="flex items-center justify-center w-full sm:w-auto sm:inline-flex min-h-[44px] px-5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold cursor-pointer">
            Choose files
          </label>
          <p className="text-xs text-gray-500 mt-2">On a phone you can take a photo of each page.</p>
        </div>
      )}

      {q.length > 0 && (
        <ul className="space-y-2" aria-live="polite">
          {q.map(item => (
            <li key={item.key} className="border border-gray-200 bg-white rounded-lg px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{item.name}</div>
                  <div className="text-xs text-gray-500">{fmtBytes(item.size)}</div>
                </div>
                <StatusPill status={item.status} />
              </div>
              {item.status === 'uploading' && (
                <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={item.pct} aria-label={`Sending ${item.name}`}>
                  <div className="h-full bg-sky-500 transition-all" style={{ width: `${item.pct}%` }} />
                </div>
              )}
              {item.error && <div className="mt-1.5 text-sm text-red-700">{item.error}</div>}
              {item.status === 'failed' && (
                <div className="mt-1 flex gap-3">
                  {item.file && !closedMsg && <button type="button" onClick={() => retry(item.key)} className="text-sm font-medium text-primary-700 min-h-[44px] px-1">Retry</button>}
                  <button type="button" onClick={() => removeFromQueue(item.key)} className="text-sm text-gray-600 min-h-[44px] px-1">Remove</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {stalled && open && (
        <button type="button" onClick={() => { setError(''); pump() }}
          className="w-full min-h-[44px] rounded-lg bg-primary-600 text-white font-semibold">
          Send {waiting} waiting {waiting === 1 ? 'file' : 'files'}
        </button>
      )}
      {uploading && <p className="text-sm text-gray-600">Sending — please keep this page open.  You can submit once it finishes.</p>}

      {receipts.length > 0 && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
          <div className="text-sm font-semibold text-emerald-900 mb-1">Sent to the firm</div>
          <ul className="space-y-1">
            {receipts.map(s => (
              <li key={s.key} className="text-sm text-emerald-900 min-w-0">
                <span className="font-medium break-all">✓ {s.name}</span>
                <span className="text-emerald-800/80"> · {fmtBytes(s.size)}{s.duplicate ? ' · already had it' : ''}</span>
              </li>
            ))}
          </ul>
          {received != null && <p className="text-xs text-emerald-800 mt-2">{received} {received === 1 ? 'file' : 'files'} received so far.</p>}
          <p className="text-xs text-emerald-800/80 mt-1">This list is on this page only — it is not saved on your device.</p>
        </div>
      )}
    </div>
  )
}
